import React, { useEffect, useMemo, useState } from 'react'
import {
  FaAddressBook,
  FaCheck,
  FaChevronDown,
  FaExclamationTriangle,
  FaMinus,
  FaSearch,
  FaSync,
  FaWhatsapp,
} from 'react-icons/fa'
import whatsappApi from './whatsappApi'
import SessionManager from '../../Utils/SessionManager'
import myToast from '../../Utils/MyToast'

const WA = '#25D366'
const WA_DARK = '#128C7E'

const thStyle = {
  padding: '8px 10px',
  fontWeight: 800,
  fontSize: 10,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#0f766e',
  background: 'linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%)',
  borderBottom: '1px solid #86efac',
  whiteSpace: 'nowrap',
}

const tdStyle = {
  padding: '8px 10px',
  verticalAlign: 'middle',
  borderBottom: '1px solid #eef2f7',
}

const selectWrap = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: 0,
}

const selectStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  height: 40,
  padding: '0 36px 0 14px',
  borderRadius: 12,
  border: '1px solid #d1d5db',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
  fontSize: 13,
  fontWeight: 700,
  color: '#0f172a',
  outline: 'none',
  cursor: 'pointer',
  minWidth: 160,
}

const searchStyle = {
  height: 40,
  padding: '0 14px 0 36px',
  borderRadius: 12,
  border: '1px solid #d1d5db',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
  fontSize: 13,
  fontWeight: 600,
  color: '#0f172a',
  outline: 'none',
  minWidth: 210,
  width: 230,
}

// Created By Cursor
function studentInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Created By Cursor
function buildWaVerifyUrl(waTo, phone, message) {
  const raw = String(waTo || phone || '').replace(/\D/g, '')
  if (!raw) return null
  return `https://wa.me/${raw}?text=${encodeURIComponent(message)}`
}

// Created By Cursor
function recalcContactSummary(students) {
  const list = students || []
  return {
    total_students: list.length,
    format_ok: list.filter((s) => s.format_valid).length,
    invalid_format: list.filter((s) => s.contact_number && !s.format_valid).length,
    no_number: list.filter((s) => !s.contact1 && !s.contact2 && !s.contact_number).length,
    on_whatsapp: list.filter(
      (s) =>
        s.is_whatsapp1 === 1 ||
        s.is_whatsapp2 === 1 ||
        s.is_whatsapp_number === 1 ||
        s.whatsapp_status === 'valid'
    ).length,
    not_on_whatsapp: list.filter((s) => s.whatsapp_status === 'invalid').length,
    unknown: list.filter(
      (s) => s.whatsapp_status === 'unknown' || s.whatsapp_status === 'format_ok'
    ).length,
  }
}

// Created By Cursor
function ContactLine({
  label,
  phone,
  waTo,
  first,
  isWhatsApp,
  formatValid,
  onVerify,
  onMarkOnWhatsApp,
  onCancelMark,
  pendingMark,
  verifying,
}) {
  const canVerify = phone && formatValid !== false && !isWhatsApp && onVerify
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: first ? 0 : 5,
        padding: '7px 10px',
        minHeight: 36,
        borderRadius: 8,
        background: pendingMark
          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          : phone
            ? '#f0fdf4'
            : '#f8fafc',
        border: pendingMark
          ? `1px solid ${WA}`
          : phone
            ? '1px solid #bbf7d0'
            : '1px dashed #e2e8f0',
        lineHeight: 1.25,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: '#fff',
          fontWeight: 800,
          minWidth: 24,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 5,
          background: phone ? WA_DARK : '#94a3b8',
          flexShrink: 0,
        }}
      >
        {label === 'Contact1' ? 'C1' : 'C2'}
      </span>
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 800,
          color: phone ? '#0f172a' : '#94a3b8',
          fontSize: 13,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {phone || '—'}
        {waTo ? (
          <span style={{ color: '#64748b', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>
            +{waTo}
          </span>
        ) : null}
      </span>
      {canVerify && !pendingMark ? (
        <button
          type="button"
          onClick={onVerify}
          disabled={verifying}
          style={{
            ...btnVerify,
            opacity: verifying ? 0.65 : 1,
            flexShrink: 0,
          }}
          title="Open WhatsApp with verification message"
        >
          <FaWhatsapp size={11} style={{ marginRight: 4 }} />
          Verify
        </button>
      ) : null}
      {canVerify && pendingMark ? (
        <>
          <button
            type="button"
            onClick={onMarkOnWhatsApp}
            disabled={verifying}
            style={{
              ...btnMarkWa,
              opacity: verifying ? 0.65 : 1,
              flexShrink: 0,
            }}
            title="Confirm this number is on WhatsApp and update the table"
          >
            <FaCheck size={10} style={{ marginRight: 4 }} />
            {verifying ? 'Saving…' : 'Mark as On WhatsApp'}
          </button>
          <button
            type="button"
            onClick={onCancelMark}
            disabled={verifying}
            style={btnCancelMark}
            title="Cancel"
          >
            Cancel
          </button>
        </>
      ) : null}
    </div>
  )
}

// Created By Cursor
function StatusChip({ variant, first }) {
  const map = {
    yes: {
      label: 'Yes',
      icon: <FaWhatsapp size={10} />,
      bg: '#dcfce7',
      color: '#15803d',
      border: '1px solid rgba(21, 128, 61, 0.22)',
    },
    no: {
      label: 'No',
      icon: <FaMinus size={9} />,
      bg: '#f1f5f9',
      color: '#64748b',
      border: '1px solid #e2e8f0',
    },
    ok: {
      label: 'OK',
      icon: <FaCheck size={9} />,
      bg: '#e0f2fe',
      color: '#0369a1',
      border: '1px solid rgba(3, 105, 161, 0.2)',
    },
    bad: {
      label: 'Bad',
      icon: <FaExclamationTriangle size={9} />,
      bg: '#fef3c7',
      color: '#b45309',
      border: '1px solid rgba(180, 83, 9, 0.2)',
    },
    empty: {
      label: '—',
      icon: null,
      bg: '#f8fafc',
      color: '#94a3b8',
      border: '1px dashed #e2e8f0',
    },
  }
  const s = map[variant] || map.empty
  return (
    <div
      style={{
        marginTop: first ? 0 : 5,
        display: 'flex',
        justifyContent: 'center',
        minHeight: 36,
        alignItems: 'center',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '4px 10px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 800,
          background: s.bg,
          color: s.color,
          border: s.border,
          minWidth: 56,
        }}
      >
        {s.icon}
        {s.label}
      </span>
    </div>
  )
}

// Created By Cursor
function PrettySelect({ value, onChange, style, children, label }) {
  return (
    <div style={selectWrap} title={label}>
      <select value={value} onChange={onChange} style={{ ...selectStyle, ...style }}>
        {children}
      </select>
      <FaChevronDown
        size={10}
        style={{
          position: 'absolute',
          right: 12,
          color: '#64748b',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}

// Created By Cursor
function ContactsTable({
  rows,
  showClassName,
  onVerifyContact,
  onMarkOnWhatsApp,
  onCancelMark,
  pendingMarkKey,
  verifyContactKey,
}) {
  return (
    <div
      className="table-responsive"
      style={{
        border: '1px solid #86efac',
        borderRadius: 12,
        background: '#fff',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
        overflow: 'hidden',
      }}
    >
      <table
        className="table mb-0"
        style={{ fontSize: 12, borderCollapse: 'separate', borderSpacing: 0, minWidth: 780 }}
      >
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <tr>
            <th style={{ ...thStyle, width: 48, textAlign: 'center' }}>#</th>
            {showClassName ? (
              <th style={{ ...thStyle, minWidth: 110 }}>Class</th>
            ) : null}
            <th style={{ ...thStyle, minWidth: 160 }}>Student</th>
            <th style={{ ...thStyle, minWidth: 240 }}>Contact</th>
            <th style={{ ...thStyle, width: 88, textAlign: 'center' }}>Format</th>
            <th style={{ ...thStyle, width: 96, textAlign: 'center' }}>WhatsApp</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, index) => {
            const contact1 = s.contact1 || ''
            const contact2 = s.contact2 || ''
            const wa1 =
              s.is_whatsapp1 === 1 || s.is_whatsapp1 === '1' || s.is_whatsapp1 === true
            const wa2 =
              s.is_whatsapp2 === 1 || s.is_whatsapp2 === '1' || s.is_whatsapp2 === true
            const fmt1 =
              s.contact1_format_valid === true ||
              s.contact1_format_valid === 1 ||
              s.contact1_format_valid === '1'
            const fmt2 =
              s.contact2_format_valid === true ||
              s.contact2_format_valid === 1 ||
              s.contact2_format_valid === '1'
            const odd = index % 2 === 1
            const baseBg = odd ? '#f8fafc' : '#fff'
            return (
              <tr
                key={s._rowKey || `${s.student_id}-${index}`}
                title={
                  (s.format_errors && s.format_errors.length
                    ? s.format_errors.join('; ')
                    : '') ||
                  s.verify_message ||
                  (s.wa_to ? `API format: ${s.wa_to}` : '')
                }
                style={{ background: baseBg, transition: 'background 0.12s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#ecfdf5'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = baseBg
                }}
              >
                <td
                  style={{
                    ...tdStyle,
                    textAlign: 'center',
                    color: '#64748b',
                    fontWeight: 800,
                    fontSize: 11,
                  }}
                >
                  {index + 1}
                </td>
                {showClassName ? (
                  <td style={{ ...tdStyle, fontWeight: 800, color: WA_DARK }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(18, 140, 126, 0.1)',
                        border: '1px solid rgba(18, 140, 126, 0.2)',
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.class_label || '—'}
                    </span>
                  </td>
                ) : null}
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: 10,
                        color: '#fff',
                        background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
                      }}
                    >
                      {studentInitials(s.student_name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 800,
                          color: '#0f172a',
                          fontSize: 12.5,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 180,
                        }}
                      >
                        {s.student_name}
                      </div>
                      {s.parent_name ? (
                        <div
                          style={{
                            fontSize: 10,
                            color: '#94a3b8',
                            fontWeight: 600,
                            marginTop: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 180,
                          }}
                        >
                          {s.parent_name}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td style={tdStyle}>
                  {contact1 ? (
                    <ContactLine
                      label="Contact1"
                      phone={contact1}
                      waTo={s.contact1_wa_to}
                      first
                      isWhatsApp={wa1}
                      formatValid={fmt1}
                      pendingMark={pendingMarkKey === `${s.student_id}|1`}
                      verifying={verifyContactKey === `${s.student_id}|1`}
                      onVerify={
                        onVerifyContact
                          ? () =>
                              onVerifyContact({
                                student: s,
                                slot: 1,
                                phone: contact1,
                                waTo: s.contact1_wa_to,
                                contactId: s.contact_id1,
                                formatValid: fmt1,
                              })
                          : null
                      }
                      onMarkOnWhatsApp={
                        onMarkOnWhatsApp
                          ? () =>
                              onMarkOnWhatsApp({
                                student: s,
                                slot: 1,
                                phone: contact1,
                                contactId: s.contact_id1,
                              })
                          : null
                      }
                      onCancelMark={
                        onCancelMark ? () => onCancelMark(`${s.student_id}|1`) : null
                      }
                    />
                  ) : null}
                  {contact2 ? (
                    <ContactLine
                      label="Contact2"
                      phone={contact2}
                      waTo={s.contact2_wa_to}
                      first={!contact1}
                      isWhatsApp={wa2}
                      formatValid={fmt2}
                      pendingMark={pendingMarkKey === `${s.student_id}|2`}
                      verifying={verifyContactKey === `${s.student_id}|2`}
                      onVerify={
                        onVerifyContact
                          ? () =>
                              onVerifyContact({
                                student: s,
                                slot: 2,
                                phone: contact2,
                                waTo: s.contact2_wa_to,
                                contactId: s.contact_id2,
                                formatValid: fmt2,
                              })
                          : null
                      }
                      onMarkOnWhatsApp={
                        onMarkOnWhatsApp
                          ? () =>
                              onMarkOnWhatsApp({
                                student: s,
                                slot: 2,
                                phone: contact2,
                                contactId: s.contact_id2,
                              })
                          : null
                      }
                      onCancelMark={
                        onCancelMark ? () => onCancelMark(`${s.student_id}|2`) : null
                      }
                    />
                  ) : null}
                  {!contact1 && !contact2 ? (
                    <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>—</span>
                  ) : null}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {contact1 ? (
                    <StatusChip variant={fmt1 ? 'ok' : 'bad'} first />
                  ) : null}
                  {contact2 ? (
                    <StatusChip variant={fmt2 ? 'ok' : 'bad'} first={!contact1} />
                  ) : null}
                  {!contact1 && !contact2 ? (
                    <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 11 }}>—</span>
                  ) : null}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {contact1 ? (
                    <StatusChip variant={wa1 ? 'yes' : 'no'} first />
                  ) : null}
                  {contact2 ? (
                    <StatusChip variant={wa2 ? 'yes' : 'no'} first={!contact1} />
                  ) : null}
                  {!contact1 && !contact2 ? (
                    <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 11 }}>—</span>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Created By Cursor
const WhatsAppStudentContactsPanel = () => {
  const [sessionId, setSessionId] = useState('')
  const [sessionLabel, setSessionLabel] = useState('')
  const [classes, setClasses] = useState([])
  const [summary, setSummary] = useState(null)
  const [classKey, setClassKey] = useState('all')
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(false)
  const [verifyContactKey, setVerifyContactKey] = useState(null)
  const [pendingMarkKey, setPendingMarkKey] = useState(null)
  const [schoolName, setSchoolName] = useState('')
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    try {
      const s = SessionManager.getCurrentSession()
      if (s && s[0]) {
        setSessionId(s[0].session_id || s[0].id || '')
        setSessionLabel(s[0].session_name || s[0].session_id || '')
      }
    } catch (e) {
      /* ignore */
    }
  }, [])

  // Created By Cursor
  const fetchContacts = async () => {
    if (!sessionId) {
      return { ok: false, error: 'Session not found' }
    }
    const res = await whatsappApi.getStudentContactsByClass({
      session_id: sessionId,
      limit: 3000,
    })
    if (res.api_status !== 'OK') {
      return { ok: false, error: res.api_remark || 'Failed to load contacts' }
    }
    return {
      ok: true,
      classes: res.data?.classes || [],
      summary: res.data?.summary || null,
      school_name: res.data?.school_name || '',
    }
  }

  // Created By Cursor
  const loadContacts = async () => {
    if (!sessionId) {
      myToast.notify_error('Session not found')
      return
    }
    setLoading(true)
    try {
      const data = await fetchContacts()
      if (!data.ok) {
        myToast.notify_error(data.error || 'Failed to load contacts')
        return
      }
      const list = data.classes
      setClasses(list)
      setSummary(data.summary)
      setSchoolName(data.school_name)
      const open = {}
      list.forEach((c, i) => {
        open[`${c.class_id}|${c.section_id}`] = i < 3
      })
      setExpanded(open)
      setClassKey('all')
    } catch (e) {
      myToast.notify_error(e.message || 'Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (sessionId) loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const visibleClasses = useMemo(() => {
    let list = classes
    if (classKey !== 'all') {
      list = list.filter((c) => `${c.class_id}|${c.section_id}` === classKey)
    }
    const q = filter.trim().toLowerCase()
    return list
      .map((c) => {
        let students = c.students || []
        if (statusFilter === 'format_ok') {
          students = students.filter((s) => s.format_valid && s.whatsapp_status !== 'valid')
        } else if (statusFilter === 'valid') {
          students = students.filter((s) => s.whatsapp_status === 'valid')
        } else if (statusFilter === 'invalid_format') {
          students = students.filter(
            (s) => s.format_valid === false && (s.contact1 || s.contact2 || s.contact_number)
          )
        } else if (statusFilter === 'no_number') {
          students = students.filter(
            (s) =>
              !(s.contact1 || s.contact2 || s.contact_number) || s.whatsapp_status === 'no_number'
          )
        } else if (statusFilter === 'invalid') {
          students = students.filter((s) => s.whatsapp_status === 'invalid')
        }
        if (q) {
          students = students.filter((s) =>
            [
              s.student_name,
              s.student_id,
              s.contact1,
              s.contact2,
              s.contact_number,
              s.parent_name,
              s.wa_to,
            ]
              .map((v) => String(v || '').toLowerCase())
              .join(' ')
              .includes(q)
          )
        }
        return { ...c, students, filteredTotal: students.length }
      })
      .filter((c) => (q || statusFilter !== 'all' ? c.filteredTotal > 0 : true))
  }, [classes, classKey, filter, statusFilter])

  const verificationMessage = useMemo(() => {
    const name = (schoolName || 'school').trim()
    return `Hello it is verification message from ${name}`
  }, [schoolName])

  // Created By Cursor — step 1: open wa.me and show Mark as On WhatsApp on that row
  const handleVerifyContact = ({ student, slot, phone, waTo, formatValid }) => {
    if (!phone) return
    if (formatValid === false) {
      myToast.notify_error('Fix number format before verify')
      return
    }
    const url = buildWaVerifyUrl(waTo, phone, verificationMessage)
    if (!url) {
      myToast.notify_error('Invalid phone number')
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
    setPendingMarkKey(`${student.student_id}|${slot}`)
    myToast.notify_success('Send the message on WhatsApp, then click Mark as On WhatsApp')
  }

  // Created By Cursor — step 2: save is_whatsapp_number and update table
  const handleMarkOnWhatsApp = async ({ student, slot, phone, contactId }) => {
    if (!phone) return
    const key = `${student.student_id}|${slot}`
    setVerifyContactKey(key)
    try {
      const res = await whatsappApi.markStudentContactVerified({
        contact_id: contactId || undefined,
        student_id: student.student_id,
        contact_number: phone,
      })
      if (res.api_status !== 'OK') {
        myToast.notify_error(res.api_remark || 'Could not update contact')
        return
      }

      setClasses((prev) => {
        const next = prev.map((cls) => ({
          ...cls,
          students: (cls.students || []).map((s) => {
            if (String(s.student_id) !== String(student.student_id)) return s
            const patch =
              slot === 1
                ? { is_whatsapp1: 1 }
                : slot === 2
                  ? { is_whatsapp2: 1 }
                  : {}
            return {
              ...s,
              ...patch,
              is_whatsapp_number: 1,
              on_whatsapp: 1,
              whatsapp_status: 'valid',
            }
          }),
        }))
        const flat = next.flatMap((c) => c.students || [])
        setSummary(recalcContactSummary(flat))
        return next
      })
      setPendingMarkKey(null)
      myToast.notify_success('Contact updated — On WhatsApp')
    } catch (e) {
      myToast.notify_error(e.message || 'Verify update failed')
    } finally {
      setVerifyContactKey(null)
    }
  }

  // Created By Cursor
  const handleCancelMark = (key) => {
    setPendingMarkKey((prev) => (prev === key ? null : prev))
  }

  const totalShown = visibleClasses.reduce((n, c) => n + (c.filteredTotal || 0), 0)

  // Created By Cursor — flatten all classes into one list (All classes = one table)
  const flatStudents = useMemo(() => {
    const rows = []
    visibleClasses.forEach((cls) => {
      ;(cls.students || []).forEach((s) => {
        rows.push({
          ...s,
          class_label:
            cls.label ||
            `${cls.class_name || cls.class_id || ''}${
              cls.section_id ? ` - ${cls.section_name || cls.section_id}` : ''
            }`,
          class_id: cls.class_id,
          section_id: cls.section_id,
          _rowKey: `${cls.class_id}|${cls.section_id}|${s.student_id}`,
        })
      })
    })
    return rows
  }, [visibleClasses])

  const showAllInOneTable = classKey === 'all'

  return (
    <div>
      <style>
        {`@keyframes waSpin { to { transform: rotate(360deg); } }
          @keyframes waFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
          .wa-sc-panel-toolbar button:hover:not(:disabled) { filter: brightness(1.03); transform: translateY(-1px); }
          .wa-sc-panel-toolbar button:active:not(:disabled) { transform: none; }
          .wa-sc-panel-toolbar button { transition: filter 0.15s ease, transform 0.15s ease, opacity 0.15s ease; }`}
      </style>

      <div
        className="wa-sc-panel-toolbar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          marginBottom: 16,
          padding: '14px 16px',
          borderRadius: 16,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 45%, #ecfeff 100%)',
          border: '1px solid #86efac',
          boxShadow: '0 6px 18px rgba(18, 140, 126, 0.08)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            color: '#0f766e',
            fontWeight: 800,
            padding: '8px 14px',
            borderRadius: 999,
            background: '#fff',
            border: '1px solid #a7f3d0',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: WA,
              boxShadow: `0 0 0 3px rgba(37, 211, 102, 0.25)`,
            }}
          />
          Session · {sessionLabel || sessionId || '—'}
        </div>

        <PrettySelect
          label="Class"
          value={classKey}
          onChange={(e) => setClassKey(e.target.value)}
          style={{ minWidth: 220, maxWidth: 280 }}
        >
          <option value="all">All classes</option>
          {classes.map((c) => (
            <option key={`${c.class_id}|${c.section_id}`} value={`${c.class_id}|${c.section_id}`}>
              {c.label} ({c.total})
            </option>
          ))}
        </PrettySelect>

        <PrettySelect
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ minWidth: 170 }}
        >
          <option value="all">All statuses</option>
          <option value="format_ok">Format OK</option>
          <option value="valid">On WhatsApp</option>
          <option value="invalid">Not on WhatsApp</option>
          <option value="invalid_format">Bad format</option>
          <option value="no_number">No number</option>
        </PrettySelect>

        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <FaSearch
            size={12}
            style={{ position: 'absolute', left: 13, color: '#94a3b8', pointerEvents: 'none' }}
          />
          <input
            type="search"
            placeholder="Search student / phone…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={searchStyle}
          />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" onClick={loadContacts} disabled={loading} style={btnSecondary}>
            <FaSync
              style={{
                marginRight: 6,
                animation: loading ? 'waSpin 0.8s linear infinite' : 'none',
              }}
            />
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {summary && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginBottom: 16,
            animation: 'waFadeIn 0.25s ease',
          }}
        >
          <SummaryChip label="Students" value={summary.total_students} />
          <SummaryChip label="On WhatsApp" value={summary.on_whatsapp} tone="ok" />
          <SummaryChip label="Format OK" value={summary.format_ok} tone="info" />
          <SummaryChip label="Bad format" value={summary.invalid_format} tone="warn" />
          <SummaryChip label="No number" value={summary.no_number} tone="neutral" />
          <SummaryChip label="Showing" value={totalShown} tone="accent" />
        </div>
      )}

      {loading && !classes.length ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: '#64748b',
            fontWeight: 700,
            borderRadius: 16,
            border: '1px dashed #cbd5e1',
            background: 'linear-gradient(180deg, #f8fafc, #fff)',
          }}
        >
          <FaSync
            style={{
              marginBottom: 10,
              color: WA_DARK,
              animation: 'waSpin 0.9s linear infinite',
            }}
          />
          <div>Loading student contacts…</div>
        </div>
      ) : visibleClasses.length === 0 || flatStudents.length === 0 ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: '#94a3b8',
            fontWeight: 700,
            borderRadius: 16,
            border: '1px dashed #cbd5e1',
            background: 'linear-gradient(180deg, #f8fafc, #fff)',
          }}
        >
          <FaAddressBook size={22} style={{ marginBottom: 8, opacity: 0.5 }} />
          <div>No contacts found for this filter</div>
        </div>
      ) : showAllInOneTable ? (
        <div style={{ animation: 'waFadeIn 0.25s ease' }}>
          <ContactsTable
            rows={flatStudents}
            showClassName
            onVerifyContact={handleVerifyContact}
            onMarkOnWhatsApp={handleMarkOnWhatsApp}
            onCancelMark={handleCancelMark}
            pendingMarkKey={pendingMarkKey}
            verifyContactKey={verifyContactKey}
          />
        </div>
      ) : (
        visibleClasses.map((cls) => {
          const key = `${cls.class_id}|${cls.section_id}`
          const isOpen = expanded[key] !== false
          const classRows = (cls.students || []).map((s) => ({
            ...s,
            class_label: cls.label,
            _rowKey: `${cls.class_id}|${cls.section_id}|${s.student_id}`,
          }))
          return (
            <div
              key={key}
              style={{
                border: '1px solid #86efac',
                borderRadius: 16,
                marginBottom: 14,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)',
                animation: 'waFadeIn 0.25s ease',
              }}
            >
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [key]: !(p[key] !== false) }))}
                style={{
                  width: '100%',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  padding: '14px 16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 800,
                    color: '#0f172a',
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fff',
                      border: '1px solid #a7f3d0',
                    }}
                  >
                    <FaAddressBook color={WA_DARK} />
                  </span>
                  {cls.label}
                  <span
                    style={{
                      fontWeight: 700,
                      color: '#0f766e',
                      fontSize: 11,
                      padding: '3px 9px',
                      borderRadius: 999,
                      background: '#fff',
                      border: '1px solid #bbf7d0',
                    }}
                  >
                    {cls.filteredTotal != null ? cls.filteredTotal : cls.total} students
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    alignItems: 'center',
                  }}
                >
                  <MiniStat label="OK" value={cls.format_ok} color="#0369a1" />
                  <MiniStat label="Bad" value={cls.invalid_format} color="#b45309" />
                  <MiniStat label="None" value={cls.no_number} color="#64748b" />
                  {cls.on_whatsapp > 0 && (
                    <MiniStat label="WA" value={cls.on_whatsapp} color={WA_DARK} />
                  )}
                  <span style={{ color: '#64748b', marginLeft: 4 }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>

              {isOpen ? (
                <div style={{ borderTop: '1px solid #bbf7d0' }}>
                  <ContactsTable
                    rows={classRows}
                    showClassName={false}
                    onVerifyContact={handleVerifyContact}
                    onMarkOnWhatsApp={handleMarkOnWhatsApp}
                    onCancelMark={handleCancelMark}
                    pendingMarkKey={pendingMarkKey}
                    verifyContactKey={verifyContactKey}
                  />
                </div>
              ) : null}
            </div>
          )
        })
      )}
    </div>
  )
}

// Created By Cursor
function SummaryChip({ label, value, tone = 'neutral' }) {
  const tones = {
    ok: {
      bg: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
      color: '#15803d',
      border: '1px solid rgba(21, 128, 61, 0.2)',
    },
    info: {
      bg: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
      color: '#0369a1',
      border: '1px solid rgba(3, 105, 161, 0.2)',
    },
    warn: {
      bg: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      color: '#b45309',
      border: '1px solid rgba(180, 83, 9, 0.2)',
    },
    accent: {
      bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
      color: '#0f766e',
      border: '1px solid #86efac',
    },
    neutral: {
      bg: '#f8fafc',
      color: '#334155',
      border: '1px solid #e2e8f0',
    },
  }
  const t = tones[tone] || tones.neutral
  return (
    <span
      style={{
        background: t.bg,
        color: t.color,
        borderRadius: 12,
        padding: '8px 14px',
        fontSize: 12,
        fontWeight: 800,
        border: t.border,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span style={{ opacity: 0.75, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 14 }}>{value ?? 0}</span>
    </span>
  )
}

// Created By Cursor
function MiniStat({ label, value, color }) {
  return (
    <span
      style={{
        color,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 999,
        padding: '3px 9px',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      {label} {value ?? 0}
    </span>
  )
}

const btnSecondary = {
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#334155',
  fontWeight: 800,
  fontSize: 13,
  padding: '0 16px',
  height: 40,
  borderRadius: 12,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
}

const btnVerify = {
  border: `1px solid ${WA_DARK}`,
  background: '#fff',
  color: WA_DARK,
  fontWeight: 800,
  fontSize: 10,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
}

const btnMarkWa = {
  border: 'none',
  background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
  color: '#fff',
  fontWeight: 800,
  fontSize: 10,
  padding: '5px 10px',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 2px 8px rgba(37, 211, 102, 0.28)',
}

const btnCancelMark = {
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#64748b',
  fontWeight: 700,
  fontSize: 10,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
}

export default WhatsAppStudentContactsPanel
